import type { Transaction } from "@rocicorp/zero";
import { type Context, userFacingError } from "../authorization";
import type { Schema } from "../generatedDrizzleSchema";
import { hasAccess } from "./access";
import { checkServiceAccountGrant } from "./serviceAccountGrantAuthorization";
import {
  type CrossResourceAvailabilityRuleId,
  crossResourceRule,
  dependentRequirementRank,
  isSupplierAvailableFromGrants,
  resolveSupplierName,
  supplierAutoGrantPermissionMessage,
  supplierRoleForGrant,
  zqlGrantReader,
} from "./crossResourceAvailability";
import { buildUserGroupMemberships, type GrantRow } from "./grantQueries";
import type { Permission } from "./permissions";
import {
  type DependentPrincipalGrant,
  insertGrantsDeduped,
  principalKey,
  principalsCrossingIntoRequirement,
} from "./validation";

type Tx = Transaction<Schema>;

// The grant's role permissions must be hydrated (GrantRow carries the customRole
// relation) so its cross-resource tier can be ranked.
type HydratedGrant = GrantRow;

export type SupplierFlag = "connectors" | "skills";

// Networks are absent: a space reaches a network ambiently, not through a member
// grant, so there is no supplier role to auto-provision — a writer lacking
// network access is still blocked by the cross-resource guard.
const AUTO_GRANTABLE = [
  {
    ruleId: "space_uses_connected_apps",
    sharePermission: "connected_app:share",
    flag: "connectors",
  },
  {
    ruleId: "space_uses_skills",
    sharePermission: "skill:write",
    flag: "skills",
  },
] as const satisfies ReadonlyArray<{
  ruleId: CrossResourceAvailabilityRuleId;
  sharePermission: Permission;
  flag: SupplierFlag;
}>;

// Grants the minimal supplier role on every connector/skill the space uses to
// each principal whose requirement this change strengthened — the same
// principals the closing cross-resource guard enforces, so it isn't triggered
// by, nor does it silently grant access to, unrelated pre-existing members.
// Inserts only the missing grants — it never rewrites a supplier's grant list,
// so a pre-existing grant on the supplier (including a service account's) is
// neither re-authorized nor clobbered. The caller must hold the supplier's
// share permission or the whole transaction rolls back with a message naming
// the supplier (not a bare "not found").
export async function applySpaceSupplierAutoGrant(opts: {
  tx: Tx;
  ctx: Context;
  spaceId: string;
  existingSpaceGrants: readonly HydratedGrant[];
  desiredSpaceGrants: readonly HydratedGrant[];
  flags: Record<SupplierFlag, boolean>;
}): Promise<void> {
  const { tx, ctx, spaceId, existingSpaceGrants, desiredSpaceGrants, flags } =
    opts;
  const grantReader = zqlGrantReader(tx, ctx.tenantId);

  const memberships = await resolveMemberships(tx, ctx, desiredSpaceGrants);

  for (const { ruleId, sharePermission, flag } of AUTO_GRANTABLE) {
    if (!flags[flag]) continue;

    const crossing = principalsCrossingIntoRequirement(
      ruleId,
      existingSpaceGrants,
      desiredSpaceGrants
    );
    if (crossing.length === 0) continue;

    const edges =
      (await crossResourceRule(ruleId).usageEdges?.(grantReader, {
        dependentId: spaceId,
      })) ?? [];
    const supplierIds = new Set(edges.map((e) => e.supplierId));

    for (const supplierId of supplierIds) {
      const supplierGrants = await grantReader.grantsForScope(supplierId);
      // A crossing principal already satisfying the required tier (e.g. via a
      // group grant, or a direct grant strong enough) needs nothing; one who
      // merely holds a weaker grant does — keying on whether the requirement is
      // met, not on whether any grant exists, so an upgrade still gets its
      // stronger supplier role. Availability is judged per principal so the
      // minimal role is computed only for those who lack it.
      const lacking = crossing.filter(
        (g) =>
          !isSupplierAvailableFromGrants(
            ruleId,
            [g],
            supplierGrants,
            memberships
          )
      );
      const additions = minimalSupplierGrants(ruleId, lacking);
      if (additions.length === 0) continue;

      const canShare = await hasAccess({
        tx,
        userId: ctx.userId,
        principal: ctx.principal,
        scopeId: supplierId,
        tenantId: ctx.tenantId,
        permission: sharePermission,
      });
      if (!canShare) {
        const name = await resolveSupplierName(tx, ruleId, supplierId);
        throw userFacingError(
          supplierAutoGrantPermissionMessage(name, sharePermission),
          { forbidden: true }
        );
      }

      // Run the service-account escalation guard exactly as the explicit share
      // path does, so auto-grant can't confer a permission onto an assumable
      // service account that the caller doesn't hold on the supplier — even
      // though it holds the supplier's share permission. Additions are always a
      // builtin supplier role (never custom), so the resolved role is direct.
      for (const addition of additions) {
        if (!addition.serviceAccountId) continue;
        const denial = await checkServiceAccountGrant({
          tx,
          ctx,
          scopeId: supplierId,
          role: { builtinRoleId: addition.builtinRoleId, customRole: null },
        });
        if (denial) {
          const name = await resolveSupplierName(tx, ruleId, supplierId);
          throw userFacingError(
            denial.reason === "missing_permission"
              ? supplierAutoGrantPermissionMessage(name, denial.permission)
              : [
                  `You can’t auto-grant access to “${name}”: it would grant a service account "`,
                  { permission: "allow_all" },
                  '", which you can’t confer',
                ],
            { forbidden: true }
          );
        }
      }

      await insertGrantsDeduped(tx, additions, supplierId, ctx.tenantId);
    }
  }
}

export function minimalSupplierGrants(
  ruleId: CrossResourceAvailabilityRuleId,
  grants: readonly DependentPrincipalGrant[]
) {
  const strongest = new Map<
    string,
    { grant: DependentPrincipalGrant; rank: number }
  >();
  for (const g of grants) {
    const key = principalKey(g);
    if (!key) continue;
    const rank = dependentRequirementRank(ruleId, g);
    if (rank === Number.POSITIVE_INFINITY) continue;
    const prev = strongest.get(key);
    if (!prev || rank < prev.rank) strongest.set(key, { grant: g, rank });
  }

  const additions = [];
  for (const { grant } of strongest.values()) {
    const builtinRoleId = supplierRoleForGrant(ruleId, grant);
    if (!builtinRoleId) continue;
    additions.push({
      userGroupId: grant.userGroupId ?? undefined,
      userId: grant.userId ?? undefined,
      serviceAccountId: grant.serviceAccountId ?? undefined,
      builtinRoleId,
    });
  }
  return additions;
}

// Each desired user's group memberships (the group scope ids they hold a grant
// on), so a member who gets supplier access via a group isn't seen as lacking it
// — matching the guard's own membership resolution.
async function resolveMemberships(
  tx: Tx,
  ctx: Context,
  desired: readonly HydratedGrant[]
): Promise<ReadonlyMap<string, readonly string[]>> {
  const grantReader = zqlGrantReader(tx, ctx.tenantId);
  const userIds = [
    ...new Set(desired.map((g) => g.userId).filter((id): id is string => !!id)),
  ];
  const grants: { userId: string; scopeId: string }[] = [];
  for (const userId of userIds) {
    const principalGrants = await grantReader.grantsForPrincipal({ userId });
    for (const g of principalGrants)
      grants.push({ userId, scopeId: g.scopeId });
  }
  return buildUserGroupMemberships(grants);
}
